import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

const COLUMN_VISIBILITY_STORAGE_KEY = "shared-table-column-visibility";

const getSavedColumnVisibilityByRoute = () =>
  JSON.parse(localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY) || "{}");

const getColumnVisibilityStorageScope = (pathname, tabValue) =>
  tabValue ? `${pathname}::${tabValue}` : pathname;

const getColumnId = (column) => column.id || column.key || column.accessor;

const getColumnName = (column) =>
  column.name || column.label || column.Header || getColumnId(column);

const getColumnHeadersSettingByRoute = (pathname, tabValue, defaultColumnHeadersSetting) => {
  const savedColumnVisibility =
    getSavedColumnVisibilityByRoute()[getColumnVisibilityStorageScope(pathname, tabValue)] || [];

  return defaultColumnHeadersSetting.map((columnHeader) => ({
    ...columnHeader,
    visible:
      savedColumnVisibility.find((savedColumn) => savedColumn.id === columnHeader.id)?.visible ??
      true,
  }));
};

export function useColumnVisibility(columns, tabValue) {
  const { pathname } = useLocation();
  const defaultColumnHeadersSetting = useMemo(
    () =>
      columns.map((column) => ({
        id: getColumnId(column),
        name: getColumnName(column),
        visible: true,
      })),
    [columns]
  );
  const [columnHeadersSetting, setColumnHeadersSetting] = useState(() =>
    getColumnHeadersSettingByRoute(pathname, tabValue, defaultColumnHeadersSetting)
  );

  useEffect(() => {
    setColumnHeadersSetting(
      getColumnHeadersSettingByRoute(pathname, tabValue, defaultColumnHeadersSetting)
    );
  }, [defaultColumnHeadersSetting, pathname, tabValue]);

  useEffect(() => {
    const storageScope = getColumnVisibilityStorageScope(pathname, tabValue);

    localStorage.setItem(
      COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify({
        ...getSavedColumnVisibilityByRoute(),
        [storageScope]: columnHeadersSetting.map(({ id, visible }) => ({ id, visible })),
      })
    );
  }, [columnHeadersSetting, pathname, tabValue]);

  const handleChangeColumnHeaderVisible = useCallback((columnId) => {
    setColumnHeadersSetting((prev) =>
      prev.map((columnHeader) => {
        if (columnHeader.id === columnId) {
          return {
            ...columnHeader,
            visible: !columnHeader.visible,
          };
        }

        return columnHeader;
      })
    );
  }, []);

  const isColumnVisible = useCallback(
    (columnId) =>
      columnHeadersSetting.find((columnHeader) => columnHeader.id === columnId)?.visible ?? true,
    [columnHeadersSetting]
  );

  const visibleColumns = useMemo(
    () => columns.filter((column) => isColumnVisible(getColumnId(column))),
    [columns, isColumnVisible]
  );

  return {
    columnHeadersSetting,
    handleChangeColumnHeaderVisible,
    visibleColumns,
  };
}

export function ColumnVisibilityMenu({ columnHeadersSetting, onChangeColumnHeaderVisible }) {
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);

  const handleOpenFilterMenu = (event) => setFilterMenuAnchorEl(event.currentTarget);
  const handleCloseFilterMenu = () => setFilterMenuAnchorEl(null);

  return (
    <>
      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={handleOpenFilterMenu}
        sx={{
          height: "37.5px",
          minHeight: "37.5px",
          mb: 0,
          px: 2,
          whiteSpace: "nowrap",
        }}
      >
        欄位篩選顯示
      </MDButton>
      <Menu
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleCloseFilterMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxHeight: "min(360px, calc(100vh - 120px))",
            overflowY: "auto",
          },
        }}
      >
        {columnHeadersSetting.map((columnHeader) => (
          <MDBox key={columnHeader.id} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Checkbox
              checked={columnHeader.visible}
              onChange={() => onChangeColumnHeaderVisible(columnHeader.id)}
            />
            {columnHeader.name}
          </MDBox>
        ))}
      </Menu>
    </>
  );
}

ColumnVisibilityMenu.propTypes = {
  columnHeadersSetting: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.node.isRequired,
      visible: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onChangeColumnHeaderVisible: PropTypes.func.isRequired,
};
